import React, { useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import api, { brl, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { Plus, Image as ImageIcon, Trophy, DollarSign, Users, Ticket, Wand2, Trash2, X, Video, Film, Gift } from "lucide-react";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [raffles, setRaffles] = useState([]);
  const [affiliates, setAffiliates] = useState([]);

  const load = async () => {
    const [s, r, a] = await Promise.all([
      api.get("/admin/stats").catch(() => ({ data: null })),
      api.get("/raffles").catch(() => ({ data: [] })),
      api.get("/admin/affiliates").catch(() => ({ data: { top_affiliates: [] } })),
    ]);
    setStats(s.data);
    setRaffles(r.data);
    setAffiliates(a.data?.top_affiliates || []);
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-400">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-200/80 mb-2">Área do Administrador</p>
          <h1 className="heading text-3xl sm:text-4xl font-extrabold text-slate-50">Painel <span className="gold-gradient-text">RMC</span></h1>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-slate-900/50 border border-white/5 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="admin-tab-overview" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200">Visão Geral</TabsTrigger>
          <TabsTrigger value="raffles" data-testid="admin-tab-raffles" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200">Gerenciar Rifas</TabsTrigger>
          <TabsTrigger value="create" data-testid="admin-tab-create" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200">Criar Rifa</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200">Relatórios</TabsTrigger>
          <TabsTrigger value="affiliates" data-testid="admin-tab-affiliates" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200">Afiliados</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <Overview stats={stats} />
        </TabsContent>
        <TabsContent value="raffles" className="mt-6">
          <ManageRaffles raffles={raffles} onChange={load} />
        </TabsContent>
        <TabsContent value="create" className="mt-6">
          <CreateRaffle onCreated={load} />
        </TabsContent>
        <TabsContent value="reports" className="mt-6">
          <Reports stats={stats} />
        </TabsContent>
        <TabsContent value="affiliates" className="mt-6">
          <AffiliatesPanel affiliates={affiliates} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const AffiliatesPanel = ({ affiliates }) => (
  <div className="card-luxury rounded-2xl p-6">
    <div className="flex items-center gap-2 mb-4">
      <Gift className="w-5 h-5 text-emerald-300" />
      <h3 className="heading text-lg font-bold text-slate-50">Ranking de Afiliados</h3>
    </div>
    {affiliates.length === 0 ? (
      <p className="text-slate-400 text-center py-10">Nenhuma comissão gerada ainda.</p>
    ) : (
      <div className="space-y-2">
        {affiliates.map((a, i) => (
          <div key={a.user_id} className="flex items-center justify-between rounded-lg border border-white/5 bg-slate-950/40 p-3">
            <div className="flex items-center gap-3">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "gold-btn" : "bg-slate-800 text-slate-300"}`}>{i + 1}</span>
              <div>
                <p className="text-slate-100 text-sm font-semibold">{a.name}</p>
                <p className="text-xs text-slate-500 font-mono">{a.ref_code} · {a.email}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="heading gold-gradient-text font-bold">{brl(a.total_earned)}</div>
              <div className="text-[10px] text-slate-500 font-mono">{a.conversions} conv · {a.referrals} indic</div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const Overview = ({ stats }) => {
  if (!stats) return <div className="text-slate-400">Carregando métricas...</div>;
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={<DollarSign className="w-5 h-5" />} label="Receita total" value={brl(stats.revenue)} />
        <Kpi icon={<Ticket className="w-5 h-5" />} label="Cotas vendidas" value={stats.quotas_sold.toLocaleString('pt-BR')} />
        <Kpi icon={<Users className="w-5 h-5" />} label="Usuários" value={stats.total_users} />
        <Kpi icon={<Trophy className="w-5 h-5" />} label="Rifas ativas" value={stats.active_raffles} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="card-luxury rounded-2xl p-6">
          <h3 className="heading text-lg font-bold text-slate-50 mb-4">Receita mensal</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={stats.revenue_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="month" stroke="#64748B" />
                <YAxis stroke="#64748B" />
                <Tooltip contentStyle={{ background: "#0B0D12", border: "1px solid rgba(230,198,135,0.3)", color: "#F8FAFC" }} />
                <Line type="monotone" dataKey="revenue" stroke="#E6C687" strokeWidth={2} dot={{ fill: "#E6C687" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-luxury rounded-2xl p-6">
          <h3 className="heading text-lg font-bold text-slate-50 mb-4">Top rifas</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={stats.top_raffles}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="raffle" stroke="#64748B" tick={{ fontSize: 10 }} />
                <YAxis stroke="#64748B" />
                <Tooltip contentStyle={{ background: "#0B0D12", border: "1px solid rgba(230,198,135,0.3)", color: "#F8FAFC" }} />
                <Bar dataKey="revenue" fill="#10B981" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

const Kpi = ({ icon, label, value }) => (
  <div className="card-luxury rounded-2xl p-5">
    <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-200 mb-3">{icon}</div>
    <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
    <div className="heading text-2xl font-bold text-slate-50 mt-1">{value}</div>
  </div>
);

const CreateRaffle = ({ onCreated }) => {
  const [form, setForm] = useState({
    title: "", description: "", prize_value: 0, quota_price: 0.5, total_quotas: 10000,
    draw_date: "", category: "LUXO"
  });
  const [images, setImages] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const fileRef = useRef();
  const videoRef = useRef();

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.url]);
      }
      toast.success("Imagens enviadas!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 100 * 1024 * 1024) {
      toast.error("Vídeo excede 100MB. Escolha um menor.");
      return;
    }
    setVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVideoUrl(data.url);
      toast.success("Vídeo enviado!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no upload do vídeo");
    } finally { setVideoUploading(false); if (videoRef.current) videoRef.current.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/raffles", { ...form, images, video_url: videoUrl || null });
      toast.success("Rifa criada com sucesso!");
      setForm({ title: "", description: "", prize_value: 0, quota_price: 0.5, total_quotas: 10000, draw_date: "", category: "LUXO" });
      setImages([]);
      setVideoUrl("");
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar rifa");
    }
  };

  return (
    <form onSubmit={submit} data-testid="admin-create-raffle-form" className="card-luxury rounded-3xl p-6 grid md:grid-cols-2 gap-5">
      <div className="md:col-span-2">
        <Label className="text-slate-300">Título da rifa</Label>
        <Input data-testid="input-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-slate-950 border-white/10 text-slate-100" placeholder="Ex.: BMW X6 M Competition 0km" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-slate-300">Descrição</Label>
        <Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-slate-950 border-white/10 text-slate-100" rows={3} />
      </div>
      <div>
        <Label className="text-slate-300">Valor do prêmio (R$)</Label>
        <Input data-testid="input-prize" type="number" step="0.01" required value={form.prize_value} onChange={(e) => setForm({ ...form, prize_value: parseFloat(e.target.value) })} className="mt-1.5 bg-slate-950 border-white/10 text-slate-100" />
      </div>
      <div>
        <Label className="text-slate-300">Preço da cota (R$)</Label>
        <Input data-testid="input-quota-price" type="number" step="0.01" required value={form.quota_price} onChange={(e) => setForm({ ...form, quota_price: parseFloat(e.target.value) })} className="mt-1.5 bg-slate-950 border-white/10 text-slate-100" />
      </div>
      <div>
        <Label className="text-slate-300">Total de cotas</Label>
        <Input data-testid="input-total-quotas" type="number" required value={form.total_quotas} onChange={(e) => setForm({ ...form, total_quotas: parseInt(e.target.value) })} className="mt-1.5 bg-slate-950 border-white/10 text-slate-100" />
      </div>
      <div>
        <Label className="text-slate-300">Data do sorteio</Label>
        <Input data-testid="input-draw-date" type="date" required value={form.draw_date} onChange={(e) => setForm({ ...form, draw_date: e.target.value })} className="mt-1.5 bg-slate-950 border-white/10 text-slate-100" />
      </div>
      <div>
        <Label className="text-slate-300">Categoria</Label>
        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
          <SelectTrigger className="mt-1.5 bg-slate-950 border-white/10 text-slate-100" data-testid="select-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-950 border-white/10 text-slate-100">
            <SelectItem value="AUTO">AUTO</SelectItem>
            <SelectItem value="LUXO">LUXO</SelectItem>
            <SelectItem value="TECH">TECH</SelectItem>
            <SelectItem value="PIX">PIX</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="md:col-span-2">
        <Label className="text-slate-300">Fotos da rifa</Label>
        <div className="mt-2 flex flex-wrap gap-3">
          {images.map((im, i) => (
            <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-white/10">
              <img src={`${process.env.REACT_APP_BACKEND_URL}${im}`} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/90 text-white flex items-center justify-center">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <label className="w-24 h-24 rounded-lg border-2 border-dashed border-white/15 flex flex-col items-center justify-center text-slate-400 hover:border-amber-400/50 hover:text-amber-200 cursor-pointer transition-all">
            <ImageIcon className="w-5 h-5" />
            <span className="text-[10px] mt-1">{uploading ? "Enviando..." : "Adicionar"}</span>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFile} data-testid="upload-photos" />
          </label>
        </div>
      </div>

      <div className="md:col-span-2">
        <Label className="text-slate-300">Vídeo do produto (opcional, até 100MB)</Label>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {videoUrl ? (
            <div className="relative w-56 rounded-lg overflow-hidden border border-white/10 bg-slate-950">
              <video
                src={`${process.env.REACT_APP_BACKEND_URL}${videoUrl}`}
                controls
                className="w-full h-32 object-cover"
              />
              <button
                type="button"
                onClick={() => setVideoUrl("")}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center"
                data-testid="remove-video"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <label className="w-56 h-32 rounded-lg border-2 border-dashed border-white/15 flex flex-col items-center justify-center text-slate-400 hover:border-emerald-400/50 hover:text-emerald-200 cursor-pointer transition-all">
              <Video className="w-6 h-6" />
              <span className="text-xs mt-1">{videoUploading ? "Enviando vídeo..." : "Adicionar vídeo"}</span>
              <span className="text-[10px] mt-0.5 text-slate-500">mp4, webm, mov</span>
              <input
                ref={videoRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={handleVideo}
                data-testid="upload-video"
              />
            </label>
          )}
          <p className="text-xs text-slate-500 max-w-xs">
            Suba um vídeo curto mostrando o prêmio. Os visitantes poderão assistir direto na página da rifa.
          </p>
        </div>
      </div>

      <div className="md:col-span-2">
        <Button data-testid="submit-create-raffle" type="submit" className="gold-btn rounded-full h-12 px-8">
          <Plus className="w-4 h-4 mr-2" /> Criar Rifa
        </Button>
      </div>
    </form>
  );
};

const ManageRaffles = ({ raffles, onChange }) => {
  const [drawing, setDrawing] = useState(null);
  const [uploadingVideoFor, setUploadingVideoFor] = useState(null);

  const drawRaffle = async (id) => {
    setDrawing(id);
    try {
      const { data } = await api.post(`/raffles/${id}/draw`);
      toast.success(`Sorteio realizado! Bilhete #${String(data.winner_ticket).padStart(6, "0")} de ${data.winner_name}`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no sorteio");
    } finally { setDrawing(null); }
  };

  const removeRaffle = async (id) => {
    if (!confirm("Excluir esta rifa?")) return;
    try {
      await api.delete(`/raffles/${id}`);
      toast.success("Rifa removida");
      onChange?.();
    } catch (e) {
      toast.error("Falha ao remover");
    }
  };

  const updateQuotas = async (id, v) => {
    const n = parseInt(v);
    if (isNaN(n) || n < 1) return;
    try {
      await api.patch(`/raffles/${id}`, { total_quotas: n });
      toast.success("Quantidade atualizada");
      onChange?.();
    } catch { toast.error("Falha ao atualizar"); }
  };

  const uploadVideoFor = async (raffleId, file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Vídeo excede 100MB.");
      return;
    }
    setUploadingVideoFor(raffleId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/raffles/${raffleId}`, { video_url: data.url });
      toast.success("Vídeo atualizado!");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar vídeo");
    } finally { setUploadingVideoFor(null); }
  };

  const removeVideo = async (raffleId) => {
    try {
      await api.patch(`/raffles/${raffleId}`, { video_url: null });
      toast.success("Vídeo removido");
      onChange?.();
    } catch { toast.error("Falha"); }
  };

  return (
    <div className="space-y-4">
      {raffles.map((r) => (
        <div key={r.raffle_id} className="card-luxury rounded-2xl p-5 flex flex-col md:flex-row gap-4 md:items-center">
          <div className="w-full md:w-32 aspect-video rounded-lg overflow-hidden border border-white/10 flex-shrink-0">
            {r.images?.[0] && (
              <img src={r.images[0].startsWith("http") ? r.images[0] : `${process.env.REACT_APP_BACKEND_URL}${r.images[0]}`} alt="" className="w-full h-full object-cover" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="heading text-lg font-semibold text-slate-50">{r.title}</h3>
              <Badge className="bg-slate-950 border border-amber-500/30 text-amber-200 text-[10px]">{r.category}</Badge>
              <Badge className={r.status === "drawn" ? "bg-emerald-500/15 border border-emerald-400/30 text-emerald-200" : "bg-amber-500/10 border border-amber-500/30 text-amber-200"}>
                {r.status === "drawn" ? "Sorteada" : "Ativa"}
              </Badge>
            </div>
            <p className="text-xs text-slate-400 mt-1">Sorteio: {fmtDate(r.draw_date)} · Prêmio: <span className="gold-gradient-text font-bold">{brl(r.prize_value)}</span></p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-400">Cotas:</span>
              <input
                type="number"
                defaultValue={r.total_quotas}
                onBlur={(e) => updateQuotas(r.raffle_id, e.target.value)}
                className="w-24 h-8 rounded bg-slate-950 border border-white/10 text-amber-200 font-mono text-xs px-2"
                data-testid={`edit-quotas-${r.raffle_id}`}
              />
              <span className="text-xs text-slate-500 font-mono">({r.sold_quotas} vendidas)</span>

              {r.video_url ? (
                <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200">
                  <Film className="w-3 h-3" /> Vídeo ativo
                  <button onClick={() => removeVideo(r.raffle_id)} className="ml-1 text-red-300 hover:text-red-200" data-testid={`remove-video-${r.raffle_id}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ) : (
                <label className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border border-white/10 text-slate-300 hover:border-emerald-400/40 hover:text-emerald-200 cursor-pointer transition-all">
                  <Video className="w-3 h-3" />
                  {uploadingVideoFor === r.raffle_id ? "Enviando..." : "Adicionar vídeo"}
                  <input
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => uploadVideoFor(r.raffle_id, e.target.files?.[0])}
                    data-testid={`upload-video-${r.raffle_id}`}
                  />
                </label>
              )}
            </div>
            {r.status === "drawn" && r.winner_ticket && (
              <p className="text-xs text-emerald-300 mt-1">🏆 Vencedor: {r.winner_name} · Bilhete #{String(r.winner_ticket).padStart(6, "0")}</p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              disabled={r.status === "drawn" || drawing === r.raffle_id}
              onClick={() => drawRaffle(r.raffle_id)}
              className="gold-btn rounded-full"
              data-testid={`draw-btn-${r.raffle_id}`}
            >
              <Wand2 className="w-4 h-4 mr-1" /> {drawing === r.raffle_id ? "Sorteando..." : "Realizar Sorteio"}
            </Button>
            <Button size="sm" variant="outline" className="border-red-500/30 bg-transparent text-red-300 hover:bg-red-500/10 rounded-full" onClick={() => removeRaffle(r.raffle_id)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

const Reports = ({ stats }) => {
  if (!stats) return null;
  return (
    <div className="card-luxury rounded-2xl p-6">
      <h3 className="heading text-lg font-bold text-slate-50 mb-4">Relatório Financeiro Detalhado</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Kpi icon={<DollarSign className="w-5 h-5" />} label="Receita bruta" value={brl(stats.revenue)} />
        <Kpi icon={<Ticket className="w-5 h-5" />} label="Cotas vendidas" value={stats.quotas_sold.toLocaleString('pt-BR')} />
        <Kpi icon={<Trophy className="w-5 h-5" />} label="Sorteios realizados" value={stats.drawn_raffles} />
      </div>
      <div className="mt-6">
        <h4 className="text-sm uppercase tracking-widest text-amber-200 mb-3">Ranking de Rifas</h4>
        <div className="space-y-2">
          {stats.top_raffles.map((r, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-white/5 bg-slate-950/40 p-3">
              <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full gold-btn flex items-center justify-center text-xs font-bold">{i + 1}</span>
                <span className="text-slate-100">{r.raffle}</span>
              </div>
              <div className="text-right">
                <div className="heading gold-gradient-text font-bold">{brl(r.revenue)}</div>
                <div className="text-[10px] text-slate-500 font-mono">{r.quantity} cotas</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
